import { useCallback, useEffect, useState } from 'react';
import i18n, {
  APP_INTERFACE_TRANSLATIONS_ENABLED,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from './config';

const DEFAULT_LOCALE: SupportedLocale = 'en';

function isSupported(code: string | null | undefined): code is SupportedLocale {
  return !!code && (SUPPORTED_LOCALES as readonly string[]).includes(code);
}

/**
 * Normalises any locale-like string (e.g. `en-US`, `zh-Hant`) down to a
 * supported base code, falling back to English if unknown.
 */
function normalise(raw: string | null | undefined): SupportedLocale {
  if (!raw) return DEFAULT_LOCALE;
  const base = raw.split('-')[0]?.toLowerCase();
  return isSupported(base) ? base : DEFAULT_LOCALE;
}

/**
 * Hook that resolves the user's preferred locale on mount, applies it to
 * i18next, and exposes a setter that persists the choice.
 *
 * Persistence is handled by i18next's built-in LanguageDetector (configured
 * in src/i18n/config.ts under storage key `hsy.locale`). This hook does not
 * read or write localStorage directly.
 *
 * Graceful by design: if i18n ever throws during init/change, the hook still
 * reports `isReady` so the app can render English rather than getting stuck.
 */
export function useLocalePreference() {
  const [locale, setLocaleState] = useState<SupportedLocale>(() =>
    normalise(i18n.language ?? DEFAULT_LOCALE),
  );
  const [isReady, setIsReady] = useState(false);

  // Resolve the preferred locale on mount. The LanguageDetector has already
  // populated i18n.language from storage/navigator by the time this runs.
  useEffect(() => {
    let cancelled = false;

    const resolved = APP_INTERFACE_TRANSLATIONS_ENABLED ? normalise(i18n.language) : DEFAULT_LOCALE;

    const applyAndFinish = () => {
      if (cancelled) return;
      setLocaleState(resolved);
      setIsReady(true);
    };

    try {
      if (!APP_INTERFACE_TRANSLATIONS_ENABLED && typeof window !== 'undefined') {
        window.localStorage.removeItem('hsy.locale');
      }

      if (i18n.language !== resolved) {
        void i18n
          .changeLanguage(resolved)
          .then(applyAndFinish)
          .catch(applyAndFinish);
      } else {
        applyAndFinish();
      }
    } catch {
      // If i18n blew up, still flip isReady so the app renders (English).
      applyAndFinish();
    }

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep local state in sync with external language changes.
  useEffect(() => {
    const handler = (lng: string) => {
      setLocaleState(normalise(lng));
    };
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, []);

  const setLocale = useCallback((code: string) => {
    const next = APP_INTERFACE_TRANSLATIONS_ENABLED ? normalise(code) : DEFAULT_LOCALE;
    // The detector persists to localStorage under `hsy.locale` automatically.
    try {
      if (!APP_INTERFACE_TRANSLATIONS_ENABLED && typeof window !== 'undefined') {
        window.localStorage.removeItem('hsy.locale');
      }
      void i18n.changeLanguage(next).catch(() => {
        /* swallow — app continues in previous language */
      });
    } catch {
      // no-op
    }
  }, []);

  return { locale, setLocale, isReady };
}

export default useLocalePreference;
